import { memo, useState, useEffect, useRef } from 'react';
import { Sparkles, ChevronDown, ScanLine, Shuffle } from 'lucide-react';
import { TAILLES, CIBLE_CARTES, CIBLE_VARIE } from '../../utils/compositionAuto';

/**
 * « Composer N questions » : bouton en contour (visible sans concurrencer
 * les cartes) + petit menu (deux cibles, réglage de N).
 * Composant de présentation pur, mémoïsé : il ne connaît ni le panier ni les
 * générateurs, il appelle `onComposer(cible)` et `onTaille(n)`.
 * Le menu se ferme à Échap et au clic dehors (écouteurs posés seulement
 * quand il est ouvert).
 */
const ComposerAutoBouton = memo(function ComposerAutoBouton({ taille, onTaille, cartesPossible, variePossible, onComposer }) {
    const [ouvert, setOuvert] = useState(false);
    const racine = useRef(null);

    useEffect(() => {
        if (!ouvert) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setOuvert(false); };
        const onClic = (e) => { if (racine.current && !racine.current.contains(e.target)) setOuvert(false); };
        window.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onClic);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClic);
        };
    }, [ouvert]);

    const choisir = (cible) => { setOuvert(false); onComposer(cible); };

    return (
        <div ref={racine} className="relative">
            <button
                type="button"
                onClick={() => setOuvert((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={ouvert}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-blue-500 text-blue-700 text-xs font-bold hover:bg-blue-50 transition"
            >
                <Sparkles size={13} />
                Composer {taille} questions
                <ChevronDown size={12} className={`transition-transform ${ouvert ? 'rotate-180' : ''}`} />
            </button>
            {ouvert && (
                <div role="menu" className="absolute right-0 top-full mt-1.5 z-40 w-72 bg-white rounded-xl border border-gray-200 shadow-xl p-1.5">
                    <button
                        type="button"
                        role="menuitem"
                        disabled={!cartesPossible}
                        onClick={() => choisir(CIBLE_CARTES)}
                        title={cartesPossible ? undefined : 'Aucune carte de cet attendu ne propose de QCM à réponse unique'}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm font-semibold text-gray-700 hover:bg-blue-50 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed transition"
                    >
                        <ScanLine size={15} className="flex-shrink-0" />
                        <span>{taille} QCM pour les cartes</span>
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        disabled={!variePossible}
                        onClick={() => choisir(CIBLE_VARIE)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm font-semibold text-gray-700 hover:bg-blue-50 disabled:text-gray-300 disabled:cursor-not-allowed transition"
                    >
                        <Shuffle size={15} className="flex-shrink-0" />
                        <span>{taille} exercices variés <span className="font-normal text-gray-400">(tous formats)</span></span>
                    </button>
                    <div className="flex items-center gap-2 px-3 pt-2 pb-1 mt-1 border-t border-gray-100">
                        <span className="text-xs text-gray-400">Nombre</span>
                        <div role="radiogroup" aria-label="Nombre de questions" className="flex gap-1">
                            {TAILLES.map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    role="radio"
                                    aria-checked={taille === n}
                                    onClick={() => onTaille(n)}
                                    className={`w-8 h-7 rounded-md text-xs font-bold transition ${
                                        taille === n ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default ComposerAutoBouton;
